import React, { useCallback } from "react"
import { Tooltip } from "antd"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCircleInfo,
  faDownload,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons"

import { Loader } from "shared/ui/Loader/Loader.component"
import { HashChip } from "shared/ui/HashChip/HashChip.component"
import {
  useProjectVersionQuery,
  useDriftQuery,
} from "../api/useProvenanceQueries"
import { useProjectOwner } from "../api/useProjectOwner"
import { collapseRemaps } from "../utils/historyLabels"
import { ChangeRow } from "./ChangeRow.component"
import { downloadJSON } from "utils/files/downloadUtils"
import { failureOf } from "utils/errMsgOf"
import { PROJECT_TAB_KEYS } from "constants/projectTabs"
import { ROUTES } from "constants/routes"
import { ACCENT } from "constants/brand"

const Stamp: React.FC<{ label: string; hash: string; hint: string; accent?: boolean }> = ({
  label,
  hash,
  hint,
  accent,
}) => (
  <div
    className={`rounded-lg border p-3 ${
      accent ? "border-[var(--dq-brand)] bg-[var(--dq-brand-soft)]" : "border-slate-200 bg-slate-50"
    }`}
  >
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      <span>{label}</span>
      <Tooltip title={hint} placement="top">
        <FontAwesomeIcon
          icon={faCircleInfo}
          className="cursor-help text-[11px] text-slate-400 transition-colors hover:text-slate-600"
        />
      </Tooltip>
    </div>
    <div className="mt-1">
      <HashChip hash={hash} length={16} className="!text-[12px]" />
    </div>
  </div>
)

const ComponentRow: React.FC<{ label: string; hash: string }> = ({ label, hash }) => (
  <div className="flex items-center justify-between border-b border-slate-100 py-1.5 last:border-0">
    <span className="text-[12px] text-slate-600">{label}</span>
    {hash ? (
      <HashChip hash={hash} length={12} />
    ) : (
      <span className="font-mono text-[11px] text-slate-400">none</span>
    )}
  </div>
)

interface StampAndDriftProps {
  projectId: string
  projectName?: string
}

/**
 * Provenance ▸ Version and data check. The project's current version ID (with its Comparison
 * and Structure IDs and the four parts it is made from) and the differences between the
 * questionnaire and the loaded data: the two computed "current state" views, in one place.
 */
const StampAndDrift: React.FC<StampAndDriftProps> = ({ projectId, projectName }) => {
  const owner = useProjectOwner()
  // Cached reads: checkpoint saves, imports and exports invalidate them, so this panel
  // refreshes itself; the Refresh button just refetches.
  const {
    data: pv,
    error: pvFailure,
    isLoading: loadingPv,
    refetch: refetchPv,
  } = useProjectVersionQuery(projectId)
  const {
    data: drift,
    isError: driftError,
    refetch: refetchDrift,
  } = useDriftQuery(projectId)
  // As the rows show them: no code changes beside an added, removed or moved answer
  const differences = drift ? collapseRemaps(drift.drift) : []

  const navigate = useNavigate()

  const load = useCallback(() => {
    refetchPv()
    refetchDrift()
  }, [refetchPv, refetchDrift])

  // The version ID as the server worked it out, with the project it belongs to. An export's
  // record (from the export dialog) is the one that names a history entry.
  const downloadVersionDetails = useCallback(() => {
    if (!pv) return
    const safe = (projectName || projectId || "project").replace(/[^a-zA-Z0-9_-]+/g, "_")
    downloadJSON(
      {
        project: { id: projectId, name: projectName ?? "", owner },
        readAt: new Date().toISOString(),
        ...pv,
      },
      `${safe}.version-details.json`
    )
  }, [pv, projectId, projectName, owner])

  if (loadingPv && !pv) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader fontSize={36} color={ACCENT} />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-3 flex justify-end">
        <button
          type="button"
          onClick={load}
          className="dq-btn"
        >
          <FontAwesomeIcon icon={faRotateRight} />
          Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-3">
      {pv && !pv.comparabilityKey && !pv.projectVersion ? (
        <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-[13px] text-slate-500">
          No version ID yet. It appears once the project has a questionnaire, data or saved
          queries.
        </div>
      ) : pv ? (
        <>
          <Stamp
            label="Comparison ID"
            hash={pv.comparabilityKey}
            accent
            hint="Made from the questionnaire, the datamap and the saved queries, not the data. Two waves with the same Comparison ID are defined the same way, so their results can be compared directly."
          />
          {pv.structuralKey && (
            <Stamp
              label="Structure ID"
              hash={pv.structuralKey}
              hint="The Comparison ID without the question wording. Two waves with the same Structure ID but different Comparison IDs differ only in wording."
            />
          )}
          <Stamp
            label="Version ID"
            hash={pv.projectVersion}
            hint="The Comparison ID plus the data itself. It pins exactly what a deliverable was made from."
          />
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Made from
            </div>
            <ComponentRow label="Questionnaire" hash={pv.components.qnr} />
            <ComponentRow label="Datamap" hash={pv.components.datamap} />
            <ComponentRow label="Saved queries" hash={pv.components.queryDefs} />
            <ComponentRow label="Data" hash={pv.components.data} />
          </div>
          <button
            type="button"
            onClick={downloadVersionDetails}
            className="dq-btn dq-btn--primary"
          >
            <FontAwesomeIcon icon={faDownload} />
            Download version details (.json)
          </button>
        </>
      ) : (
        <div className="py-8 text-center text-[13px] text-slate-500">
          {pvFailure
            ? `Couldn’t work out the version ID: ${failureOf(pvFailure)}`
            : "Couldn’t work out the version ID."}
        </div>
      )}
        </div>

        {/* Differences between the questionnaire and the data */}
        <div className="self-start rounded-lg border border-slate-200 p-3" role="status">
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Differences between the questionnaire and the data
        </div>
        {driftError ? (
          <p className="text-[12px] font-medium text-rose-600">
            Couldn’t check: the server didn’t respond. Try refreshing.
          </p>
        ) : !drift ? (
          <p className="text-[12px] italic text-slate-400">Checking…</p>
        ) : !drift.hasQnr ? (
          <p className="text-[12px] italic text-slate-400">No questionnaire, so there is nothing to compare.</p>
        ) : drift.parseStatus === "parse_failed" ? (
          <p className="text-[12px] italic text-slate-400">Couldn’t compare against the questionnaire.</p>
        ) : !drift.hasData ? (
          <p className="text-[12px] italic text-slate-400">No data imported yet, so there is nothing to compare.</p>
        ) : differences.length === 0 ? (
          <p className="text-[12px] font-medium text-emerald-700">
            ✓ The data matches the questionnaire.
          </p>
        ) : (
          <>
            <p className="mb-1 text-[12px] font-medium text-amber-700">
              ⚠ {differences.length} difference{differences.length === 1 ? "" : "s"} from the questionnaire
            </p>
            <ul className="max-h-[420px] overflow-auto rounded-md border border-slate-100 px-2">
              {differences.map((c, i) => (
                <ChangeRow key={i} change={c} />
              ))}
            </ul>
          </>
        )}
        {/* Drift answers "does the design still match the shape of the data?", which is the
            question users arrive here with only half of — the other half is "did the right
            people answer?". That one is routing-derived, lives on Data Checks, and is otherwise
            hard to discover from here. Gated like DriftBanner: both sides must exist, or the
            link is a dead end. Gated on a SUCCEEDED comparison too — after a parse failure or a
            server error the base check would hit the same wall, so inviting it is a false lead. */}
        {drift?.hasQnr && drift.hasData && drift.parseStatus === "ok" && !driftError && (
          <p className="mt-2 border-t border-slate-100 pt-2 text-[11px] leading-snug text-slate-400">
            Drift compares the datamap shapes. To check the loaded responses against the
            questionnaire’s routing,{" "}
            <button
              type="button"
              onClick={() => navigate(`${ROUTES.PROJECT(projectId)}?tab=${PROJECT_TAB_KEYS.DATA_CHECKS}`)}
              className="text-[11px] text-slate-500 underline hover:text-slate-700"
            >
              run a base check
            </button>
            .
          </p>
        )}
      </div>
      </div>
    </div>
  )
}

export default StampAndDrift
